import { IQuestion } from 'app/interfaces/IQuestion'
import { QuestionComponent } from './QuestionComponent'
import { IStatement } from 'app/interfaces/IStatement'
import { UntypedFormGroup } from '@angular/forms'
import { IRule } from 'app/interfaces/IRule'
import { IMinMaxAnswers } from 'app/interfaces/IMinMaxAnswers'
import { LoremIpsum } from 'lorem-ipsum'
import { ViewerTypeEnum } from 'app/enums/ViewerTypeEnum'

export abstract class Question implements IQuestion {
  private readonly _isOptional: boolean
  private readonly _groupName: string
  private readonly _matrixIntro: Record<string, string>
  private readonly _matrixUniq: boolean
  private readonly _question: Record<string, string>
  private readonly _randomFlags: string
  private readonly _randomizeGroup: boolean
  private readonly _randomizeQuestion: boolean
  private readonly _randomizeStatements: boolean
  private readonly _refName: string
  private readonly _shortLabel: Record<string, string>
  private readonly _viewerType: ViewerTypeEnum
  private readonly _extra_raw: Record<string, {}>
  private readonly _favorite: boolean
  private readonly _matrixGroup: string
  private readonly _pageBreak: boolean
  private readonly _imageSize: number
  private readonly _createUser: Record<string, string>
  private readonly _rules: {
    stopIf?: IRule
    visibleIf?: IRule
    hideIf?: IRule
  }
  private readonly _positionLocked: boolean
  private readonly _shouldSaveInExtProfile: boolean
  private readonly _statementsRulesIndex: string[]
  private readonly _limitAnswersAmount: boolean
  private readonly _maxAnswers: IMinMaxAnswers
  private readonly _minAnswers: IMinMaxAnswers
  private readonly _key: string
  private readonly _useUniqueMentionLabel: boolean
  private readonly _useNumericalValues: boolean
  private readonly _numericalValuesUnity: string
  private readonly _disableOpenStatementsRealtimeValidation: boolean
  public reverseMobile: boolean
  private readonly _commentsControlName: string = 'comments'
  private readonly _formControlName: string = 'selected'
  private readonly _imageControlName: string = 'image'
  private readonly _audioControlName: string = 'audio'

  private _statementsOrder: string[]
  private _statements: Record<string, IStatement>

  constructor(question: IQuestion) {
    this._limitAnswersAmount = question.limitAnswersAmount
    this._groupName = question.groupName
    this._key = question.key
    this._imageSize = question.imageSize
    this._matrixIntro = question.matrixIntro
    this._matrixUniq = question.matrixUniq
    this._question = question.question
    this._randomFlags = question.randomFlags
    this._randomizeGroup = question.randomizeGroup
    this._randomizeQuestion = question.randomizeQuestion
    this._randomizeStatements = question.randomizeStatements
    this.reverseMobile = question.reverseMobile
    this._refName = question.refName
    this._shortLabel = question.shortLabel
    this._viewerType = question.viewerType
    this._matrixGroup = question.matrixGroup
    this._favorite = question.favorite
    this._extra_raw = question.extra_raw
    this._pageBreak = question.pageBreak
    this._statements = question.statements
    this._rules = question.rules
    this._statementsOrder = question.statementsOrder
    this._isOptional = question.isOptional
    this._positionLocked = question.positionLocked
    this._shouldSaveInExtProfile = question.shouldSaveInExtProfile
    this._statementsRulesIndex = question.statementsRulesIndex
    this._maxAnswers = question.maxAnswers
    this._minAnswers = question.minAnswers
    this._useUniqueMentionLabel = question.useUniqueMentionLabel
    this._createUser = question.createUser
    this._numericalValuesUnity = question.numericalValuesUnity
    this._useNumericalValues = question.useNumericalValues
    this._disableOpenStatementsRealtimeValidation = question.disableOpenStatementsRealtimeValidation
    this.orderStatements()
  }

  abstract buildFormGroupValidators(): any[]
  abstract getDisplayComponent(): QuestionComponent
  abstract buildQuestionFormGroup(): UntypedFormGroup
  abstract fillFormWithDummyAnswer(surveyForm: UntypedFormGroup, iterator: number, maxRetries: number): void
  abstract applyMaxAnswers(surveyForm: UntypedFormGroup): void
  abstract formatFormPayload(surveyForm: UntypedFormGroup): Record<string, {}>

  keepInSurveyAnswers(): boolean {
    return true
  }

  generateLoremIpsum(): string {
    return new LoremIpsum({
      wordsPerSentence: {
        max: 16,
        min: 4
      }
    }).generateSentences(4);
  }

  setEverySelectedControlToTrueForGivenRow(surveyForm: UntypedFormGroup, rowKey: string) {
    this.statementKeys.forEach(sKey => {
      surveyForm.controls[this.key].get(rowKey).get(this.formControlName).get(sKey).setValue(true, {emitEvent: false})
    })
  }

  extractRandomObjectProperty(obj: Record<string, {}>) {
    const keys = Object.keys(obj);
    return keys[Math.floor(Math.random() * keys.length)];
  }

  private orderStatements() {
    if (!this.statementsOrder) {
      return
    }
    let reorderedStatements: Record<string, IStatement> = {}
    this.statementsOrder.forEach(key => {
      if (this.statements[key]) {
        reorderedStatements[key] = this.statements[key]
      }
    })
    this._statements = reorderedStatements
  }

  set statements(statements: Record<string, IStatement>) {
    this._statements = statements
  }

  set statementsOrder(statementsOrder) {
    this._statementsOrder = statementsOrder
  }

  get shouldSaveInExtProfile() {
    return this._shouldSaveInExtProfile
  }

  get statementsOrder() {
    return this._statementsOrder
  }

  get rules() {
    return this._rules
  }

  get extra_raw() {
    return this._extra_raw
  }

  get pageBreak() {
    return this._pageBreak
  }

  get matrixGroup() {
    return this._matrixGroup
  }

  get favorite() {
    return this._favorite
  }

  get statements() {
    return this._statements
  }

  get groupName() {
    return this._groupName
  }

  get key() {
    return this._key
  }

  get matrixIntro() {
    return this._matrixIntro
  }

  get matrixUniq() {
    return this._matrixUniq
  }

  get question() {
    return this._question
  }

  get randomFlags() {
    return this._randomFlags
  }

  get randomizeGroup() {
    return this._randomizeGroup
  }

  get randomizeQuestion() {
    return this._randomizeQuestion
  }

  get refName() {
    return this._refName
  }

  get shortLabel() {
    return this._shortLabel
  }

  get createUser() {
    return this._createUser
  }

  get viewerType() {
    return this._viewerType
  }

  get imageSize() {
    return this._imageSize
  }

  get formControlName() {
    return this._formControlName
  }

  get imageControlName() {
    return this._imageControlName
  }

  get audioControlName() {
    return this._audioControlName
  }

  get statementKeys() {
    return Object.keys(this._statements)
  }

  get positionLocked() {
    return this._positionLocked
  }

  get isOptional() {
    return this._isOptional
  }

  get statementsRulesIndex() {
    return this._statementsRulesIndex
  }

  get limitAnswersAmount() {
    return this._limitAnswersAmount
  }

  get randomizeStatements() {
    return this._randomizeStatements
  }

  get maxAnswers() {
    return this._maxAnswers
  }

  get commentsControlName() {
    return this._commentsControlName
  }

  get useUniqueMentionLabel() {
    return this._useUniqueMentionLabel
  }

  get minAnswers() {
    return this._minAnswers
  }

  get useNumericalValues() {
    return this._useNumericalValues
  }

  get numericalValuesUnity() {
    return this._numericalValuesUnity
  }

  get disableOpenStatementsRealtimeValidation() {
    return this._disableOpenStatementsRealtimeValidation
  }
}
